import { useState, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { TooltipProvider } from '@/components/ui/Tooltip'
import { Toaster } from 'sonner'
import { ApiError } from '@/api/client'
import { BrandingProvider } from '@/lib/branding'
import { env } from '@/lib/env'

/**
 * Root provider tree.
 *
 * Single QueryClient per app instance, configured with:
 *   - 60s stale time so route navigations don't hammer the API
 *   - exponential retry up to 3 attempts on networking errors only
 *   - no retry on 4xx (auth/permission/validation are not transient)
 */
export function AppProviders({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            // Under VITE_USE_MOCKS (Playwright, Storybook, demo) every
            // request is either deterministically mocked or genuinely
            // missing — retrying just slows the suite by ~7s per
            // route (exponential backoff to 3 attempts) without
            // changing the outcome. Disable so failures surface
            // immediately and tests don't time out waiting on
            // doomed retries.
            retry: env.VITE_USE_MOCKS
              ? false
              : (failureCount, err) => {
                  if (err instanceof ApiError && err.status >= 400 && err.status < 500)
                    return false
                  return failureCount < 3
                },
          },
          mutations: {
            retry: false,
          },
        },
      }),
  )

  return (
    <QueryClientProvider client={client}>
      <BrandingProvider>
        <TooltipProvider delayDuration={300}>
          {children}
          <Toaster richColors closeButton position="top-right" />
        </TooltipProvider>
        {import.meta.env.DEV && !env.VITE_HIDE_DEVTOOLS && (
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        )}
      </BrandingProvider>
    </QueryClientProvider>
  )
}
